import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Input, Modal, Select, Space, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Copy, Pencil, Plus } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { AccessCodeStatus, DEFAULT_ACCESS_CODE_EXPIRY_HOURS, ACCESS_CODE_RELOGIN_WINDOW_DAYS } from '@evaltick/shared';
import { AdminAccessCodesService, BaseAPIClient, type AccessCode } from '@/services/api';
import { DataTable } from '@/components/DataTable';
import { Button as AppButton } from '@/components/Button';
import { ROUTES } from '@/routes';
import { formatDateTime } from '@/utils';
import styles from './AdminPackages.module.scss';

const { Text } = Typography;

const PAGE_SIZE = 10;

const STATUS_TAG_COLOR: Record<AccessCodeStatus, string> = {
  [AccessCodeStatus.Unused]: 'blue',
  [AccessCodeStatus.Used]: 'green',
  [AccessCodeStatus.Revoked]: 'red',
  [AccessCodeStatus.Expired]: 'default',
};

const STATUS_LABEL: Record<AccessCodeStatus, string> = {
  [AccessCodeStatus.Unused]: 'Unused',
  [AccessCodeStatus.Used]: 'Used',
  [AccessCodeStatus.Revoked]: 'Revoked',
  [AccessCodeStatus.Expired]: 'Expired',
};

// Revoking changes something in exactly two cases: an Unused code stops being
// redeemable, and a Used code's guest loses their session on their next request.
// Revoked is already a no-op, and Expired here means never-redeemed-and-past-expiry
// — nobody can redeem it and nobody holds a session from it.
const isRevocable = (status: AccessCodeStatus): boolean =>
  status === AccessCodeStatus.Unused || status === AccessCodeStatus.Used;

// Only a Used code has a live session to cut, so only that revoke needs a
// confirmation — it can drop a candidate mid-assessment.
const cutsLiveSession = (status: AccessCodeStatus): boolean => status === AccessCodeStatus.Used;

interface AccessCodesCardProps {
  packageId: string;
}

export const AccessCodesCard: React.FC<AccessCodesCardProps> = ({ packageId }) => {
  const { getToken } = useAuth();
  const { notification, modal } = App.useApp();

  const [codes, setCodes] = useState<AccessCode[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTags, setNewTags] = useState<string[]>([]);
  const [newExpiresInHours, setNewExpiresInHours] = useState(DEFAULT_ACCESS_CODE_EXPIRY_HOURS);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [editingTagsId, setEditingTagsId] = useState<string | null>(null);
  const [editingTags, setEditingTags] = useState<string[]>([]);
  const [savingTags, setSavingTags] = useState(false);

  const getService = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('Not authenticated');
    return new AdminAccessCodesService(token);
  }, [getToken]);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const service = await getService();
      const result = await service.list(packageId, page, PAGE_SIZE);
      setCodes(result.data);
      setTotalCount(result.pagination.totalCount);
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoading(false);
    }
  }, [getService, packageId, page, notification]);

  useEffect(() => {
    void fetchCodes();
  }, [fetchCodes]);

  const openCreateModal = () => {
    setNewTags([]);
    setNewExpiresInHours(DEFAULT_ACCESS_CODE_EXPIRY_HOURS);
    setModalOpen(true);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const service = await getService();
      await service.create({ packageId, tags: newTags, expiresInHours: newExpiresInHours });
      notification.success({ message: 'Access code created' });
      setModalOpen(false);
      // Codes are newest-first, so a fresh one lands on page 1 — jump there rather
      // than refetching whatever page the admin was on.
      if (page === 1) {
        await fetchCodes();
      } else {
        setPage(1);
      }
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setCreating(false);
    }
  };

  // Saves on blur, so clicking the pencil by mistake and clicking away is a no-op
  // rather than something needing a Cancel button. Note `mode="tags"` only commits a
  // token on Enter or comma — a half-typed tag is dropped, same as the create modal.
  const saveTags = async (id: string) => {
    setSavingTags(true);
    try {
      const service = await getService();
      await service.updateTags(id, editingTags);
      // Only close the editor if it's still this row's. Clicking another row's pencil
      // blurs this one first, so by the time the save resolves the admin may already
      // have opened the next editor — clearing it outright would eat that click.
      setEditingTagsId((current) => (current === id ? null : current));
      await fetchCodes();
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setSavingTags(false);
    }
  };

  const revoke = async (id: string, hadLiveSession: boolean) => {
    setRevokingId(id);
    try {
      const service = await getService();
      await service.revoke(id);
      notification.success({
        message: hadLiveSession ? 'Access code revoked and session ended' : 'Access code revoked',
      });
      await fetchCodes();
    } catch (err) {
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setRevokingId(null);
    }
  };

  const handleRevoke = (record: AccessCode) => {
    if (!cutsLiveSession(record.status)) {
      void revoke(record.id, false);
      return;
    }
    modal.confirm({
      title: 'Revoke this code?',
      content:
        "The candidate is signed out immediately, even mid-assessment. This can't be undone — a new code makes them start from scratch.",
      okText: 'Revoke',
      okButtonProps: { danger: true, shape: 'round' },
      cancelButtonProps: { shape: 'round' },
      cancelText: 'Cancel',
      onOk: () => revoke(record.id, true),
    });
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      notification.success({ message: `${label} copied` });
    } catch {
      notification.error({ message: `Could not copy ${label.toLowerCase()}` });
    }
  };

  const redeemUrl = `${window.location.origin}${ROUTES.REDEEM}`;

  const columns: ColumnsType<AccessCode> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => (
        <Space size={4}>
          <Text code>{code}</Text>
          <Tooltip title="Copy code">
            <Button
              size="small"
              type="text"
              icon={<Copy style={{ width: '1em', height: '1em' }} />}
              onClick={() => copyToClipboard(code, 'Code')}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[], record) =>
        editingTagsId === record.id ? (
          <Select
            className={styles.codesTagsEditor}
            mode="tags"
            autoFocus
            value={editingTags}
            onChange={setEditingTags}
            onBlur={() => void saveTags(record.id)}
            disabled={savingTags}
            tokenSeparators={[',']}
          />
        ) : (
          <Space size={4}>
            {tags.length ? tags.map((tag) => <Tag key={tag}>{tag}</Tag>) : <Text type="secondary">—</Text>}
            <Tooltip title="Edit tags">
              <Button
                size="small"
                type="text"
                icon={<Pencil style={{ width: '1em', height: '1em' }} />}
                onClick={() => {
                  setEditingTags(tags);
                  setEditingTagsId(record.id);
                }}
              />
            </Tooltip>
          </Space>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const tag = <Tag color={STATUS_TAG_COLOR[record.status]}>{STATUS_LABEL[record.status]}</Tag>;
        // Revoked outranks Used in the derived status, so without this a code
        // revoked after being redeemed would lose that it was ever used.
        if (record.status === AccessCodeStatus.Revoked && record.redeemedAt) {
          return (
            <Tooltip title={`Redeemed ${formatDateTime(record.redeemedAt)} — that session has been ended`}>
              {tag}
            </Tooltip>
          );
        }
        return tag;
      },
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (value: string) => formatDateTime(value),
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Button
          size="small"
          danger
          disabled={!isRevocable(record.status)}
          loading={revokingId === record.id}
          onClick={() => handleRevoke(record)}
        >
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className={styles.sectionTitle}>Access codes</div>
      <p className={styles.sectionNote}>
        One-time codes that let a candidate start this package without a Clerk account. Share{' '}
        <Text code>{redeemUrl}</Text> and a code as two separate messages — never as one link with the
        code baked in. Once redeemed, the same code lets the candidate log back in for up to{' '}
        {ACCESS_CODE_RELOGIN_WINDOW_DAYS} days — after that, they'll need a new code to get back in.
      </p>
      <div className={styles.codesToolbar}>
        <Button className={styles.btnPrimary} icon={<Plus style={{ width: '1em', height: '1em' }} />} onClick={openCreateModal}>
          Add code
        </Button>
      </div>
      <DataTable<AccessCode>
        dataSource={codes}
        columns={columns}
        rowKey="id"
        loading={loading}
        page={page}
        totalCount={totalCount}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        locale={{ emptyText: 'No codes yet' }}
      />

      <Modal
        title="Add access code"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={
          <div className={styles.modalFooterActions}>
            <AppButton appearance="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </AppButton>
            <AppButton appearance="solid" onClick={handleCreate} loading={creating}>
              Create
            </AppButton>
          </div>
        }
      >
        <div className={styles.codesField}>
          <Text strong>Tags</Text>
          <div className={styles.codesFieldNote}>
            For admin grouping/filtering only — never shown to the candidate.
          </div>
          <Select
            className={styles.codesFieldControl}
            mode="tags"
            placeholder="e.g. RCMP 1"
            value={newTags}
            onChange={setNewTags}
            tokenSeparators={[',']}
          />
        </div>
        <div>
          <Text strong>Expires in (hours)</Text>
          <Input
            className={styles.codesFieldControl}
            type="number"
            min={1}
            value={newExpiresInHours}
            onChange={(e) =>
              setNewExpiresInHours(Math.max(1, parseInt(e.target.value, 10) || DEFAULT_ACCESS_CODE_EXPIRY_HOURS))
            }
          />
        </div>
      </Modal>
    </>
  );
};

export default AccessCodesCard;
